import { communitySlug } from "$stores/community";
import { labels, labelsFetched } from "./labels";
import { derived, type Updater, writable, get } from "svelte/store";
import { v4 as uuidv4 } from "uuid";
import api from "api";
import { alerts, type AlertPayload } from "$stores/alert";
import type { Post, Thread } from "models/forums";
import type {
  ForumThreadWithPermissions,
  LabelSerialiser,
  ThreadPostWithPermissions,
} from "@outverse-video/outverse-api-client-lib/src/api";
import { favourites } from "./favourites";
import { PAGE_SIZE } from "app/utils/consts";

export interface Reaction {
  emoji: string;
  count: number;
  userReacted: boolean;
  userReactedUuid: string;
  created: string;
  reacters: { uuid: string; handle: string }[];
}

type OpenThreadModalProps = {
  open: boolean;
  thread?: Thread;
};

/** Info related to current thread (posts, post count, etc)
 *
 * Posts are stored by per-thread sequence numbers (e.g., 1, 2, 3),
 * and these sequence numbers are also stored in the postIndex keyed by uuid.
 *
 * To retrieve a post by uuid:
 * ```ts
 * const replySeqNum = state.postIndex[post.replyToPostUuid];
 * const reply = state.posts[replySeqNum];
 * ```
 */
export interface ThreadState {
  uuid: string;
  activeThread?: ForumThreadWithPermissions;
  posts: { [id: number]: Post };
  postIndex: { [uuid: string]: number };
  replies: { [uuid: string]: number[] };
  pages: { [id: number]: string };
  currentPost: number;
  threadModal: OpenThreadModalProps;
  goToPost: number | null;
  count: number;
  pageSize: number;
}

const initialState: ThreadState = {
  uuid: "",
  activeThread: undefined,
  posts: {},
  postIndex: {},
  replies: {},
  pages: {},
  currentPost: 0,
  threadModal: { open: false },
  goToPost: null,
  count: 0,
  pageSize: 20,
};

export const transformPost = (post: ThreadPostWithPermissions) => ({
  ...post.threadPost,
  myPermissions: post.myPermissions,
});

export const transformPosts = (posts: ThreadPostWithPermissions[]): Post[] =>
  posts.map(transformPost);

const threadStore = () => {
  const { subscribe, set, update } = writable<ThreadState>(initialState);

  return {
    subscribe,
    update,
    set,
    fetch: (uuid: string, communitySlug: string) => {
      return api.communitiesThreadsRetrieve({ uuid, communitySlug }).then((resp) => {
        // We've got a deep incompatibility between these types, due to Author !== ReplierSerializer.
        // @ts-ignore
        update((thread) => ({ ...thread, activeThread: resp.data }));
      });
    },
    updateActiveThread: (partialForumThread: Partial<Thread>) => {
      update((state) => {
        if (state.activeThread) {
          state.activeThread = {
            ...state.activeThread,
            forumThread: {
              ...state.activeThread.forumThread,
              ...partialForumThread,
            },
          };
        }
        return state;
      });
    },
    addThreadReaction: async (communitySlug: string, threadUuid: string, emoji: string) => {
      update((state) => {
        addThreadReactionAsync(state, communitySlug, threadUuid, emoji, update).then();

        return state;
      });
    },
    async toggleFavourite() {
      if (get(favourite)) {
        await this.unfavourite(get(thread).uuid, get(communitySlug));
      } else {
        await this.favourite(get(thread).uuid, get(communitySlug));
      }
    },
    async favourite(uuid: string, communitySlug: string) {
      const activeThreadUuid = get(thread).uuid;
      if (uuid === activeThreadUuid) {
        this.updateActiveThread({ favourite: !get(favourite) });
      }

      await api.communitiesThreadsFavourite({ uuid, communitySlug });
      await favourites.add({ uuid, type: "thread" });
    },
    async unfavourite(uuid: string, communitySlug: string) {
      const activeThreadUuid = get(thread).uuid;
      if (uuid === activeThreadUuid) {
        this.updateActiveThread({ favourite: !get(favourite) });
      }

      await api.communitiesThreadsUnfavourite({ uuid, communitySlug });
      await favourites.remove({ uuid, type: "thread" });
    },
    updateLabels: async (labelsSerialiser: LabelSerialiser[], communitySlug: string) => {
      update((state) => {
        const labels = labelsSerialiser.map((label) => label.uuid || "");
        const currentLabels = state.activeThread!.forumThread.labels;
        // optimistically update the labels in the active thread
        state.activeThread!.forumThread.labels = labels;

        api
          .communitiesThreadsPartialUpdate({
            communitySlug: communitySlug,
            uuid: state.activeThread!.forumThread!.uuid!,
            patchedForumThread: {
              labels,
            },
          })
          .then((data) => {
            // if the labels returned from the server are different from the optimistically updated labels
            if (
              data.data.labels.sort().join(",") !==
              state.activeThread!.forumThread.labels.sort().join(",")
            ) {
              // update the labels returned from the server
              update((state) => {
                state.activeThread!.forumThread.labels = data.data.labels;
                return state;
              });
            }
          })
          .catch(() => {
            alerts.error("Failed to update label");
            // if the request fails, revert the labels to the previous state
            state.activeThread!.forumThread.labels = currentLabels;
          });

        return state;
      });
    },
    toggleThreadReaction: async (
      communitySlug: string,
      threadUuid: string,
      emoji: string,
      userReactedUuid?: string
    ) => {
      update((state) => {
        toggleThreadReactionAsync(
          state,
          communitySlug,
          threadUuid,
          emoji,
          userReactedUuid!,
          update
        ).then();

        return state;
      });
    },
    addPostReaction: async (communitySlug: string, replyUuid: string, emoji: string) => {
      update((state) => {
        addPostReactionAsync(state, communitySlug, replyUuid, emoji, update).then();
        return state;
      });
    },
    togglePostReaction: async (communitySlug: string, replyUuid: string, emoji: string) => {
      update((state) => {
        togglePostReactionAsync(state, communitySlug, replyUuid, emoji, update).then();

        return state;
      });
    },
    pinThread: () => {
      update((state) => {
        if (!state.activeThread) return state;
        state.activeThread.forumThread.pinned = true;
        return state;
      });
    },
    unpinThread: () => {
      update((state) => {
        if (!state.activeThread) return state;
        state.activeThread.forumThread.pinned = false;
        return state;
      });
    },
    lockThread: () => {
      update((state) => {
        if (!state.activeThread) return state;
        state.activeThread.forumThread.locked = true;
        return state;
      });
    },
    unlockThread: () => {
      update((state) => {
        if (!state.activeThread) return state;
        state.activeThread.forumThread.locked = false;
        return state;
      });
    },
    setUuid: (uuid: string) => {
      update((state) => {
        state.uuid = uuid;
        return state;
      });
    },
    setActivitySubscribeStatus: (isSubscribedToActivity: boolean) => {
      update((state) => {
        if (state.activeThread) {
          state.activeThread = {
            ...state.activeThread,
            forumThread: {
              ...state.activeThread.forumThread,
              isSubscribedToActivity,
            },
          };
        }
        return state;
      });
    },
    setRepliesSubscribeStatus: (isSubscribedToReplies: boolean) => {
      update((state) => {
        if (state.activeThread) {
          state.activeThread = {
            ...state.activeThread,
            forumThread: {
              ...state.activeThread.forumThread,
              isSubscribedToReplies,
            },
          };
        }
        return state;
      });
    },
    clear: () => {
      set({
        uuid: "",
        activeThread: undefined,
        posts: {},
        postIndex: {},
        replies: {},
        pages: {},
        currentPost: 0,
        threadModal: {
          open: false,
          thread: undefined,
        },
        goToPost: null,
        count: 0,
        pageSize: 20,
      });
    },
    updateCount: (count: number) => {
      update((state) => {
        return { ...state, count };
      });
    },
    addPosts: (posts: Post[]) => {
      update((state) => {
        posts.forEach((post) => {
          // store posts by per-thread sequence number and mapping uuid->seqNum for jumping
          state.posts[post.seqNum] = post;
          state.postIndex[post.uuid] = post.seqNum;
        });
        return state;
      });
    },
    /** Add a list of Posts replying to another Post.
     *
     * If the original post has no replies yet it adds one.
     */
    addReplies: (posts: Post[]) => {
      update((state) => {
        posts.forEach((post) => {
          if (!post.replyToPostUuid) return;
          const replySeqNum = state.postIndex[post.replyToPostUuid];
          const replies = state.replies[post.replyToPostUuid];

          // avoid double adding (especially on hot-reload)
          if (!!replies && post.seqNum in replies) return;

          // minor-hack: for newly created replies give the parent a count to enable tree view
          if (!state.posts[replySeqNum].repliesCount) {
            state.posts[replySeqNum].repliesCount = 1;
          }
          state.replies[post.replyToPostUuid] = [...(replies || []), post.seqNum];
        });
        return state;
      });
    },
    addPage: (pageNumber: number) => {
      update((state) => {
        if (state.goToPost) return state; // don't add pages when mid-scroll (stampeding herd of pages 🦬📑)
        state.pages = { ...state.pages, [pageNumber]: uuidv4() };
        return state;
      });
    },
    setCurrentPost: (currentPost: number) => {
      update((state) => {
        state.currentPost = currentPost;
        return state;
      });
    },
    openThreadModal: (thread?: Thread) => {
      update((state) => {
        state.threadModal = { open: true, thread };
        return state;
      });
    },
    closeThreadModal: () => {
      update((state) => {
        state.threadModal = { open: false };
        return state;
      });
    },
    goToPostByUUID: (uuid: string) => {
      update((state) => {
        state.goToPost = state.postIndex[uuid];
        const pageNumber = Math.ceil(state.goToPost / PAGE_SIZE);
        // add page to show (with a nonce for forcing refresh)
        state.pages[pageNumber] = state.pages[pageNumber] || uuidv4();
        return state;
      });
    },
    goToPostbySeqNum: (goToPost: number) => {
      update((state) => {
        state.goToPost = goToPost;
        const pageNumber = Math.ceil(goToPost / PAGE_SIZE);
        // add page to show (with a nonce for forcing refresh)
        state.pages[pageNumber] = state.pages[pageNumber] || uuidv4();
        return state;
      });
    },
    clearGoToPost: () => {
      update((state) => {
        state.goToPost = null;
        return state;
      });
    },
    deletePost: (props: DeletePostProps) => {
      update((state) => {
        deletePostAsync({ ...props, update });
        return state;
      });
    },
  };
};

interface DeletePostProps {
  uuid: string;
  addAlert: (alert: AlertPayload) => void;
}
async function deletePostAsync(
  props: DeletePostProps & { update: (updater: Updater<ThreadState>) => void }
) {
  try {
    await api.communitiesPostsDestroy({
      communitySlug: get(communitySlug),
      uuid: props.uuid,
    });
    props.update((state) => {
      const postNum = state.postIndex[props.uuid];
      const post = state.posts[postNum];
      // blank content
      state.posts[postNum] = {
        ...post,
        content: {},
        author: null,
        isRemoved: true,
      };
      return state;
    });
  } catch {
    props.addAlert({
      type: "error",
      title: `Failed to delete post`,
    });
  }
}

function optimisticAddReactionToThread(
  reaction: Reaction,
  update: (updater: Updater<ThreadState>) => void
) {
  update((state: ThreadState) => {
    if (state.activeThread) {
      // @ts-ignore
      state.activeThread.forumThread.reactions = [
        // @ts-ignore
        ...state.activeThread.forumThread.reactions,
        reaction,
      ];
    }

    return state;
  });
}

function optimisticSetReactionToThread(
  emojiObject: Reaction,
  update: (updater: Updater<ThreadState>) => void
) {
  update((state: ThreadState) => {
    const reactions = state.activeThread?.forumThread.reactions;
    if (reactions) {
      const reactionIndex = reactions.findIndex((reaction) => reaction.emoji === emojiObject.emoji);
      reactions[reactionIndex] = emojiObject;
    }

    return state;
  });
}

function updateActiveThreadReactions(
  thread: Thread,
  update: (updater: Updater<ThreadState>) => void
) {
  update((state) => {
    if (state.activeThread?.forumThread) {
      state.activeThread.forumThread.reactions = thread.reactions || [];
    }
    return state;
  });
}

async function addThreadReactionAsync(
  state: ThreadState,
  communitySlug: string,
  threadUuid: string,
  emoji: string,
  update: (updater: Updater<ThreadState>) => void
) {
  const reactions: Reaction[] | undefined = state.activeThread?.forumThread.reactions;
  if (!reactions) return state;

  for (const reaction of reactions) {
    if (reaction.emoji === emoji && reaction.userReacted) {
      return;
    }
  }

  const emojiObject: Reaction = {
    created: new Date().toISOString(),
    userReactedUuid: "",
    emoji,
    count: 1,
    userReacted: true,
    reacters: [],
  };

  optimisticAddReactionToThread(emojiObject, update);

  try {
    const resp = await api.communitiesThreadsReaction({
      communitySlug,
      uuid: threadUuid,
      reactionCreateSerialiser: { emoji },
    });
    // We've got a deep incompatibility between these types, due to Author !== ReplierSerializer.
    // @ts-ignore
    updateActiveThreadReactions(resp.data, update);
  } catch (error) {
    console.log((error as Error).message);
  }
}

async function toggleThreadReactionAsync(
  state: ThreadState,
  communitySlug: string,
  threadUuid: string,
  emoji: string,
  userReactedUuid: string,
  update: (updater: Updater<ThreadState>) => void
) {
  const reactions: Reaction[] | undefined = state.activeThread?.forumThread.reactions;
  if (!reactions) return state;

  const emojiObject: Reaction = {
    created: new Date().toISOString(),
    count: 0,
    emoji,
    userReactedUuid: "",
    userReacted: false,
    reacters: [],
  };

  for (const reaction of reactions) {
    if (reaction.emoji === emoji) {
      if (reaction.userReacted) {
        emojiObject.count = reaction.count - 1;
      } else {
        emojiObject.count = reaction.count + 1;
        emojiObject.userReacted = true;
      }
      emojiObject.created = reaction.created;
      break;
    }
  }

  optimisticSetReactionToThread(emojiObject, update);

  if (emojiObject.userReacted) {
    try {
      const resp = await api.communitiesThreadsReaction({
        communitySlug,
        uuid: threadUuid,
        reactionCreateSerialiser: { emoji },
      });
      // We've got a deep incompatibility between these types, due to Author !== ReplierSerializer.
      // @ts-ignore
      updateActiveThreadReactions(resp.data, update);
    } catch (error) {
      console.log((error as Error).message);
    }
  } else {
    try {
      await api.communitiesThreadsDeleteReaction({
        communitySlug,
        uuid: threadUuid,
        reactionUuid: userReactedUuid,
      });
    } catch (e) {
      console.error(e);
    }
  }
}

function optimisticAddReactionToPost(
  index: number,
  reaction: Reaction,
  update: (updater: Updater<ThreadState>) => void
) {
  update((state: ThreadState) => {
    if (state.posts[index]) {
      state.posts[index].reactions = [...state.posts[index].reactions, reaction];
    }

    return state;
  });
}

function optimisticSetReactionToPost(
  index: number,
  emojiObject: Reaction,
  update: (updater: Updater<ThreadState>) => void
) {
  update((state: ThreadState) => {
    const reactionIndex = state.posts[index]?.reactions.findIndex(
      (reaction) => reaction.emoji === emojiObject.emoji
    );
    if (reactionIndex) {
      state.posts[index].reactions[reactionIndex] = emojiObject;
    }
    return state;
  });
}

function updatePostReactions(
  post: Pick<Post, "seqNum" | "reactions">,
  update: (updater: Updater<ThreadState>) => void
) {
  update((state: ThreadState) => {
    state.posts[post.seqNum] = {
      ...state.posts[post.seqNum],
      reactions: post.reactions,
    };
    return state;
  });
}

async function addPostReactionAsync(
  state: ThreadState,
  communitySlug: string,
  replyUuid: string,
  emoji: string,
  update: (updater: Updater<ThreadState>) => void
) {
  const index = state.postIndex[replyUuid];
  const reactions: Reaction[] | undefined = state.posts[index]?.reactions;
  if (!reactions) {
    return state;
  }

  for (const reaction of reactions) {
    if (reaction.emoji === emoji && reaction.userReacted) {
      return;
    }
  }

  const emojiObject: Reaction = {
    created: new Date().toISOString(),
    userReactedUuid: "",
    emoji,
    count: 1,
    userReacted: true,
    reacters: [],
  };

  optimisticAddReactionToPost(index, emojiObject, update);
  try {
    const resp = await api.communitiesPostsReaction({
      communitySlug,
      uuid: replyUuid,
      reactionCreateSerialiser: { emoji },
    });
    updatePostReactions(resp.data, update);
  } catch (e) {
    console.error(e);
  }
}

async function togglePostReactionAsync(
  state: ThreadState,
  communitySlug: string,
  replyUuid: string,
  emoji: string,
  update: (updater: Updater<ThreadState>) => void
) {
  const index = state.postIndex[replyUuid];

  const reactions: Reaction[] | undefined = state.posts[index].reactions;
  if (!reactions) return state;

  const emojiObject: Reaction = {
    created: new Date().toISOString(),
    userReactedUuid: "",
    emoji,
    count: 0,
    userReacted: false,
    reacters: [],
  };

  for (const reaction of reactions) {
    if (reaction.emoji === emoji) {
      if (reaction.userReacted) {
        emojiObject.count = reaction.count - 1;
      } else {
        emojiObject.count = reaction.count + 1;
        emojiObject.userReacted = true;
      }
      emojiObject.created = reaction.created;
      emojiObject.userReactedUuid = reaction.userReactedUuid;
      break;
    }
  }

  optimisticSetReactionToPost(index, emojiObject, update);

  if (emojiObject.userReacted) {
    try {
      const resp = await api.communitiesPostsReaction({
        communitySlug,
        uuid: replyUuid,
        reactionCreateSerialiser: { emoji },
      });
      updatePostReactions(resp.data, update);
    } catch (error) {
      console.log((error as Error).message);
    }
  } else {
    try {
      const resp = await api.communitiesPostsDeleteReaction({
        communitySlug,
        reactionUuid: emojiObject.userReactedUuid!,
        uuid: replyUuid,
      });
      // @ts-ignore - the destroy endpoint does return the post
      updatePostReactions(resp.data, update);
    } catch (e) {
      console.error(e);
    }
  }
}

export async function subUnsubActivity(threadUuid: string, communitySlug: string) {
  const activeThread = get(thread).activeThread;

  if (!activeThread) {
    return;
  }

  if (activeThread.forumThread.isSubscribedToActivity) {
    await api.communitiesThreadsUnsubscribeFromActivity({
      communitySlug: communitySlug,
      uuid: threadUuid,
    });
    thread.setActivitySubscribeStatus(false);
  } else {
    await api.communitiesThreadsSubscribeToActivity({
      communitySlug: communitySlug,
      uuid: threadUuid,
    });
    thread.setActivitySubscribeStatus(true);
  }
}

export async function subUnsubReplies(threadUuid: string, communitySlug: string) {
  const activeThread = get(thread).activeThread;

  if (!activeThread) {
    return;
  }

  if (activeThread.forumThread.isSubscribedToReplies) {
    await api.communitiesThreadsUnsubscribeFromReplies({
      communitySlug: communitySlug,
      uuid: threadUuid || "",
    });
    thread.setRepliesSubscribeStatus(false);
  } else {
    await api.communitiesThreadsSubscribeToReplies({
      communitySlug: communitySlug,
      uuid: threadUuid || "",
    });
    thread.setRepliesSubscribeStatus(true);
  }
}

export const thread = threadStore();
export const activeThread = derived(thread, ($thread) => $thread.activeThread);
export const threadUuid = derived(
  activeThread,
  ($activeThread) => $activeThread?.forumThread?.uuid
);
export const goToPost = derived(thread, ($thread) => $thread.goToPost);
export const posts = derived(thread, ($thread) => $thread.posts);
export const replies = derived(thread, ($thread) => $thread.replies);
export const replyCount = derived(activeThread, ($thread) => $thread?.forumThread?.replyCount ?? 0);
export const favourite = derived(
  activeThread,
  ($thread) => $thread?.forumThread?.favourite ?? false
);
export const deletedCount = derived(
  activeThread,
  ($thread) => $thread?.forumThread?.deletedCount ?? 0
);
export const totalRepliesCount = derived(
  activeThread,
  ($thread) => ($thread?.forumThread?.replyCount ?? 0) + ($thread?.forumThread?.deletedCount ?? 0)
);
export const activeThreadLocked = derived(
  activeThread,
  ($activeThread) => $activeThread?.forumThread.locked
);
export const pages = derived(thread, ($thread) => $thread.pages);
export const pagesList = derived(pages, ($pages) =>
  Object.keys($pages)
    .map(Number)
    .sort((a, b) => a - b)
);
export const threadAuthor = derived(
  activeThread,
  ($activeThread) => $activeThread?.forumThread?.author?.uuid
);
export const activeThreadLabels = derived(
  [activeThread, labels, labelsFetched],
  ([$thread, $labels, $fetched]) =>
    $thread && $fetched ? $thread?.forumThread.labels.map((uuid) => $labels[uuid]) : []
);
